/**
 * CardContainer - Outer wrapper component for CardV2.
 */

import React, { forwardRef } from 'react';
import { styled } from 'linaria/react';

import useTheme from 'helpers/useTheme';
import { CardContainerProps } from '../types';
import { SPACING } from '../styled/tokens';
import { isColorDark, getTextPrimary, getTextSecondary } from './colorUtils';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  width: 100%;
  position: relative;
  cursor: pointer;
  padding-left: ${SPACING.cardPadding}px;
  padding-right: ${SPACING.cardPadding}px;
  margin-bottom: ${SPACING.cardGap}px;
  box-sizing: border-box;
`;

const CardContainer = forwardRef<HTMLDivElement, CardContainerProps>(
  ({ children, className, overlayBackgroundColor, onClick }, ref) => {
    const isDarkTheme = useTheme();
    // The container itself has no background fill, so text contrast is based on
    // what's visually behind the card: the overlayBackgroundColor (e.g. profile
    // gradient) if present, otherwise the page background determined by theme.
    // The card's own backgroundColor is NOT used here — it has no visible
    // background. Media cards handle their own internal contrast separately.
    const darkBg = overlayBackgroundColor ? isColorDark(overlayBackgroundColor) : isDarkTheme;
    const contrastVars = {
      '--card-text-primary': getTextPrimary(darkBg),
      '--card-text-secondary': getTextSecondary(darkBg),
    } as React.CSSProperties;
    return (
      <StyledContainer
        ref={ref}
        className={className}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        style={contrastVars}
      >
        {children}
      </StyledContainer>
    );
  }
);

CardContainer.displayName = 'CardContainer';

export default CardContainer;
